import { GitForkIcon, PinIcon } from 'lucide-react';
import type { JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import type { Skill } from '@/lib/api/skills.js';
import { cn } from '@/lib/utils.js';

import { forkLockCaption } from './fork-lock.js';
import { OneRowChipFold, type FoldChip } from './OneRowChipFold.js';

/**
 * The card's badge row in priority order — scope and status first, tags last,
 * so the one-row fold hides tags before it ever hides a status badge.
 */
function badgeChips(
  skill: Skill,
  own: boolean,
  showPinnedBadge: boolean,
): FoldChip[] {
  const scopeLabel = own
    ? 'Personal'
    : skill.scope === 'workspace'
      ? 'Workspace'
      : 'Company';
  const chips: FoldChip[] = [
    {
      key: 'scope',
      node: <Badge variant={own ? 'secondary' : 'info'}>{scopeLabel}</Badge>,
    },
  ];
  if (showPinnedBadge && skill.pinned_at !== null) {
    chips.push({
      key: 'pinned',
      node: (
        <Badge variant="secondary">
          <PinIcon
            className="fill-current"
            strokeWidth={1.75}
            aria-hidden="true"
          />
          Pinned
        </Badge>
      ),
    });
  }
  /** A disabled row only reaches a caller who can re-enable it (own private, or a workspace skill they manage), so the badge needs no separate permission input. */
  if (skill.disabled_at !== null) {
    chips.push({
      key: 'disabled',
      node: <Badge variant="destructive">Disabled</Badge>,
    });
  }
  if (!own && skill.muted_at !== null) {
    chips.push({
      key: 'muted',
      node: <Badge variant="destructive">Muted</Badge>,
    });
  }
  if (skill.forked_from_id !== null) {
    chips.push({
      key: 'forked',
      node: <Badge variant="outline">Forked</Badge>,
    });
  }
  for (const tag of skill.tags ?? []) {
    chips.push({
      key: `tag-${tag}`,
      node: <Badge variant="outline">{tag}</Badge>,
    });
  }
  return chips;
}

interface SkillCardProps {
  skill: Skill;
  onOpen: () => void;
  onTogglePin: () => void;
  pinPending: boolean;
  /**
   * Copies a published skill into the caller's own list. `null` on a skill they already own —
   * there is nothing to copy from yourself.
   */
  onFork: (() => void) | null;
  /**
   * Set when the caller already has a private twin of this trigger. Forking again could only 409,
   * so the control opens the copy instead — the same swap `SkillDetail` makes.
   */
  onOpenOwnCopy: (() => void) | null;
  forkPending: boolean;
  /**
   * Whether the badge row says "Pinned". True in the "All skills" list, where the same skill also
   * sits on the shelf above and the badge is what explains why; false inside the shelf itself,
   * where every card is pinned and the badge would only repeat the section heading.
   */
  showPinnedBadge: boolean;
}

/**
 * One skill row in the directory's Skills pane. Mirrors `ConnectorCard`'s
 * "row click opens detail, pin star is a distinct control" idiom used by
 * `DeckRow`: the title is a real `<button>` (keyboard-accessible primary
 * control), the `<article>`'s own `onClick` is a pointer-only convenience
 * that enlarges the click target, and the pin toggle stops propagation so it
 * never also opens the detail view. The pin uses the same PinIcon idiom as a
 * pinned conversation in the sidebar — filled when pinned — not a favorite star.
 */
export function SkillCard({
  skill,
  onOpen,
  onTogglePin,
  pinPending,
  onFork,
  onOpenOwnCopy,
  forkPending,
  showPinnedBadge,
}: SkillCardProps): JSX.Element {
  const own = skill.scope === 'private';
  const pinned = skill.pinned_at !== null;
  const forkAction = onOpenOwnCopy ?? onFork;
  const forkLocked = !onOpenOwnCopy && skill.can_copy === false;
  const lockCaption = forkLockCaption(skill);
  const forkLabel = forkLocked
    ? lockCaption
    : onOpenOwnCopy
      ? `Open my copy of ${skill.title}`
      : `Fork ${skill.title}`;

  return (
    <article
      onClick={onOpen}
      className="group flex cursor-pointer flex-col gap-2 rounded-xl border border-border/60 bg-card/40 p-4 transition-all hover:border-border hover:bg-card hover:shadow-sm"
      aria-label={`${skill.title} skill`}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
          className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left"
        >
          <span className="w-full truncate text-sm font-semibold text-foreground">
            {skill.title}
          </span>
          <span className="w-full truncate text-xs text-muted-foreground">
            /{skill.trigger}
          </span>
        </button>
        <div className="-mr-1 -mt-1 flex shrink-0 items-center gap-0.5">
          {forkAction !== null && (
            <span title={forkLocked ? lockCaption : undefined}>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  forkAction();
                }}
                disabled={forkPending || forkLocked}
                aria-label={forkLabel}
                title={
                  forkLocked
                    ? undefined
                    : onOpenOwnCopy
                      ? 'You already have a personal copy — open it'
                      : 'Fork into your personal skills, yours to edit'
                }
                className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                <GitForkIcon
                  className="size-4"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              </button>
            </span>
          )}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onTogglePin();
            }}
            disabled={pinPending}
            aria-pressed={pinned}
            aria-label={pinned ? `Unpin ${skill.title}` : `Pin ${skill.title}`}
            className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PinIcon
              className={cn('size-4', pinned && 'fill-current text-foreground')}
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
        {skill.description || 'No description'}
      </p>

      <OneRowChipFold
        wrapperClassName="mt-auto"
        className="gap-1.5"
        gapPx={6}
        chips={badgeChips(skill, own, showPinnedBadge)}
        renderOverflow={(hiddenCount) => (
          <Badge variant="outline">{`+${hiddenCount}`}</Badge>
        )}
      />
    </article>
  );
}
